// THE ONE FRAME DRAWER: the studio preview and the export both call
// drawFrame, so what the person sees is what the video gets. Draw order:
// background (animated or the cached still), the template's layer
// (lib/videostudio/templates.ts), captions, the Jammable mark.
import { drawCaptions, type DrawnBounds } from "./captionDraw";
import { danceMoment, portraitMoment, type CaptionWord } from "./layers";
import { ENVELOPE_RATE } from "./audio";
import {
  GRADIENTS,
  stretchAt,
  type VideoProject,
  type VoiceTime,
} from "./project";
import { drawEmbers, drawFluid, drawStars } from "./backgrounds";
import { drawTemplate, templateNamed } from "./templates";
import { drawWatermark } from "./watermark";

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export type Singer = {
  urlParam: string;
  name: string;
  photo: ImageBitmap | null;
  color: string;
};

export type Scene = {
  project: VideoProject;
  words: CaptionWord[];
  voiceTimes: VoiceTime[];
  singers: Singer[];
  /** The uploaded background image, when background.kind is "image". */
  backgroundImage: ImageBitmap | null;
  envelope: Float32Array | null;
  /** The cover's name, for templates that show it (Glide's player). */
  title: string;
  /** The "Made with jammable.com" badge (watermark.ts), drawn when `mark`. */
  badge: ImageBitmap | null;
  /** Every export carries the mark (the studio sets it for all plans). The preview shows it too. */
  mark: boolean;
};

/** The background is the same every frame; drawn once per size and reused. */
const backdrops = new WeakMap<
  Scene["project"]["background"],
  Map<string, OffscreenCanvas | HTMLCanvasElement>
>();

function makeCanvas(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

function coverFit(
  ctx: Ctx,
  img: CanvasImageSource & { width: number; height: number },
  W: number,
  H: number,
  zoom = 1,
) {
  const k = Math.max(W / img.width, H / img.height) * zoom;
  const w = img.width * k,
    h = img.height * k;
  ctx.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
}

function backdrop(scene: Scene, W: number, H: number, lead: Singer | null) {
  const bg = scene.project.background;
  let sized = backdrops.get(bg);
  if (!sized) backdrops.set(bg, (sized = new Map()));
  const key = `${W}x${H}:${lead?.urlParam ?? ""}:${scene.backgroundImage ? 1 : 0}`;
  const hit = sized.get(key);
  if (hit) return hit;

  const canvas = makeCanvas(W, H);
  const ctx = canvas.getContext("2d") as Ctx;
  const [from, to] = GRADIENTS[bg.gradient] ?? GRADIENTS[0];
  const grad = ctx.createLinearGradient(0, 0, W * 0.4, H);
  grad.addColorStop(0, from);
  grad.addColorStop(1, to);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  const picture =
    bg.kind === "image"
      ? scene.backgroundImage
      : bg.kind === "voice"
        ? (lead?.photo ?? null)
        : null;
  if (picture) {
    ctx.save();
    if (bg.blur > 0 && "filter" in ctx)
      ctx.filter = `blur(${Math.round(bg.blur * Math.min(W, H))}px)`;
    // Blur bleeds transparent edges in; overscan hides them.
    coverFit(ctx, picture, W, H, bg.blur > 0 ? 1.15 : 1);
    ctx.restore();
    ctx.fillStyle = `rgba(10, 4, 20, ${bg.dim})`;
    ctx.fillRect(0, 0, W, H);
  }
  sized.set(key, canvas);
  return canvas;
}

function loudnessAt(scene: Scene, t: number): number {
  const env = scene.envelope;
  if (!env || !env.length) return 0;
  return (
    env[Math.min(env.length - 1, Math.max(0, Math.floor(t * ENVELOPE_RATE)))] ??
    0
  );
}

function drawSinger(ctx: Ctx, W: number, H: number, scene: Scene, t: number) {
  const style = scene.project.singers;
  const n = stretchAt(scene.voiceTimes, t);
  // A solo cover has one singer the whole way; a duet hands the photo over.
  const stretch = n >= 0 ? scene.voiceTimes[n] : null;
  const singer =
    scene.singers.length === 1
      ? scene.singers[0]
      : stretch
        ? scene.singers.find((s) => s.urlParam === stretch.voice)
        : null;
  if (!singer) return;
  const since =
    stretch && scene.singers.length > 1
      ? t - stretch.start
      : t - scene.project.trim.start;
  const size = style.size * Math.min(H, W * 1.78);
  const fromLeft = n % 2 === 0;
  const cx =
    style.mode === "center"
      ? 0.5
      : fromLeft
        ? 0.06 + size / 2 / W
        : 0.94 - size / 2 / W;
  const moment = portraitMoment(style.entrance, since, fromLeft);
  const dance = danceMoment(style.dance, since);
  const pulse = 1 + loudnessAt(scene, t) * 0.06;

  ctx.save();
  ctx.globalAlpha = moment.alpha;
  ctx.translate((cx + moment.shift) * W, style.y * H - dance.rise * size);
  ctx.rotate(moment.turn + dance.turn);
  const k = moment.scale * dance.scale * pulse;
  ctx.scale(k, k);
  // The ring, then the round photo inside it.
  ctx.beginPath();
  ctx.arc(0, 0, size / 2 + size * 0.04, 0, Math.PI * 2);
  ctx.fillStyle = "#D1FE17";
  ctx.fill();
  ctx.save();
  ctx.beginPath();
  ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
  ctx.clip();
  if (singer.photo) {
    // coverFit fills a box from its top-left corner; the origin is the centre.
    ctx.translate(-size / 2, -size / 2);
    coverFit(ctx, singer.photo, size, size);
  } else {
    ctx.fillStyle = singer.color;
    ctx.fillRect(-size / 2, -size / 2, size, size);
  }
  ctx.restore();
  ctx.restore();

  if (style.names) {
    const fs = Math.round(size * 0.13);
    ctx.save();
    ctx.globalAlpha = moment.alpha;
    ctx.font = `800 ${fs}px "Arial Black", Arial, sans-serif`;
    const label = singer.name;
    const tw = Math.min(ctx.measureText(label).width, W * 0.8);
    const px = (cx + moment.shift) * W,
      py = style.y * H + size / 2 + size * 0.16;
    ctx.fillStyle = "rgba(46, 15, 82, 0.85)";
    ctx.beginPath();
    ctx.roundRect(
      px - tw / 2 - fs * 0.7,
      py - fs * 0.85,
      tw + fs * 1.4,
      fs * 1.7,
      fs * 0.85,
    );
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, px, py + fs * 0.05, W * 0.8);
    ctx.restore();
  }
}

function drawMark(ctx: Ctx, W: number, H: number) {
  const fs = Math.round(Math.min(W, H) * 0.03);
  const text = "Made on Jammable";
  ctx.save();
  ctx.font = `800 ${fs}px "Arial Black", Arial, sans-serif`;
  const tw = ctx.measureText(text).width;
  const pad = fs * 0.7;
  const x = W - tw - pad * 2 - fs,
    y = fs;
  ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
  ctx.beginPath();
  ctx.roundRect(x, y, tw + pad * 2, fs * 1.9, fs * 0.95);
  ctx.fill();
  ctx.fillStyle = "#2E0F52";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + pad, y + fs * 0.98);
  ctx.restore();
}

/** Where the editable layers landed on this frame, in its pixels (the
 * studio's stage draws selection boxes over them). */
export type DrawnLayers = { caption: DrawnBounds | null };

/** Draws the frame at song time `t` onto a W×H context. */
export function drawFrame(
  ctx: Ctx,
  W: number,
  H: number,
  scene: Scene,
  t: number,
): DrawnLayers {
  let caption: DrawnBounds | null = null;
  const kind = scene.project.background.kind;
  if (kind === "stars") drawStars(ctx, W, H, t);
  else if (kind === "embers") drawEmbers(ctx, W, H, t);
  else if (kind === "fluid") drawFluid(ctx, W, H, t);
  else {
    const n = stretchAt(scene.voiceTimes, t);
    const lead =
      scene.singers.length === 1
        ? scene.singers[0]
        : n >= 0
          ? (scene.singers.find(
              (s) => s.urlParam === scene.voiceTimes[n].voice,
            ) ?? scene.singers[0])
          : (scene.singers[0] ?? null);
    ctx.drawImage(backdrop(scene, W, H, lead ?? null), 0, 0, W, H);
  }
  // Templates set fonts, alignment and alpha freely; keep them to themselves.
  ctx.save();
  drawTemplate(ctx, W, H, scene, t, () => {
    if (scene.project.singers.on) drawSinger(ctx, W, H, scene, t);
  });
  ctx.restore();
  if (
    templateNamed(scene.project.template).captions &&
    scene.project.captions.on &&
    scene.words.length
  ) {
    caption = drawCaptions(ctx, W, H, scene.words, t, scene.project.captions);
  }
  if (scene.mark) {
    if (scene.badge) drawWatermark(ctx, W, H, scene.badge);
    else drawMark(ctx, W, H);
  }
  return { caption };
}
